import { useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import AuthShell from './AuthShell';
import { Field, Spinner } from '../../components/ui';
import { useAuth } from '../../context/AuthContext';
import { useToast } from '../../context/ToastContext';

export default function Login() {
  const { login } = useAuth();
  const toast = useToast();
  const navigate = useNavigate();
  const location = useLocation();
  const [form, setForm] = useState({ username: '', password: '', remember: false });
  const [errors, setErrors] = useState({});
  // Server-side failure copy (bad credentials, disabled account, ...). The API
  // returns it under errors.username[0]; show it verbatim above the form too so
  // it survives the toast timeout.
  const [formError, setFormError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  async function handleSubmit(e) {
    e.preventDefault();
    setErrors({});
    setFormError('');
    setSubmitting(true);
    try {
      const user = await login({
        username: form.username,
        password: form.password,
        remember: form.remember,
      });
      toast.success(`Welcome back, ${user?.name?.split(' ')[0] || ''}!`);
      const dest = location.state?.from || (user?.role === 'administrator' ? '/admin' : '/member');
      navigate(dest, { replace: true });
    } catch (err) {
      const fieldErrors = err.errors || {};
      setErrors(fieldErrors);
      // 422 credential failures arrive under `username`; fall back to the
      // normalized top-level message for anything else.
      const message =
        fieldErrors.username?.[0] || fieldErrors.password?.[0] || err.message || 'Login failed.';
      setFormError(message);
      toast.error(message);
    } finally {
      setSubmitting(false);
    }
  }

  const usernameError = errors.username?.[0];

  return (
    <AuthShell
      title="Welcome back"
      subtitle="Sign in to your member or administrator account."
      footer={
        <span>
          Forgot your password?{' '}
          <Link to="/forgot-password" className="font-semibold text-maroon-700 hover:underline">
            Reset it
          </Link>
        </span>
      }
    >
      <form onSubmit={handleSubmit} className="space-y-5">
        {formError && (
          <div
            role="alert"
            aria-live="assertive"
            className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm font-medium text-red-700"
          >
            {formError}
          </div>
        )}
        <Field
          label="Username"
          /* Skip the inline copy when the banner above already shows it. */
          error={usernameError === formError ? undefined : usernameError}
          hint="Sign in with the username your administrator gave you, not your email address."
        >
          <input
            type="text"
            className="input"
            autoComplete="username"
            autoCapitalize="none"
            autoCorrect="off"
            spellCheck="false"
            aria-invalid={usernameError ? 'true' : undefined}
            value={form.username}
            onChange={(e) => setForm({ ...form, username: e.target.value })}
            required
          />
        </Field>
        <Field label="Password" error={errors.password?.[0]}>
          <input
            type="password"
            className="input"
            autoComplete="current-password"
            value={form.password}
            onChange={(e) => setForm({ ...form, password: e.target.value })}
            required
          />
        </Field>
        <label className="flex items-center gap-2 text-sm text-ink-soft">
          <input
            type="checkbox"
            checked={form.remember}
            onChange={(e) => setForm({ ...form, remember: e.target.checked })}
            className="rounded border-gray-300 text-maroon-700 focus:ring-maroon-400"
          />
          Remember me
        </label>
        <button type="submit" className="btn-primary w-full" disabled={submitting}>
          {submitting ? <Spinner className="h-4 w-4 text-white" /> : 'Sign In'}
        </button>
      </form>
    </AuthShell>
  );
}
